import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Zap } from "lucide-react";

const TEMPLATE_WORKFLOWS = [
  {
    name: 'Client Onboarding Sequence',
    description: 'Automatically welcome new clients and set up their first project',
    trigger_event: 'client_created',
  },
  {
    name: 'Client Feedback Collection',
    description: 'Request feedback after project completion',
    trigger_event: 'project_completed',
  },
  {
    name: 'Invoice Payment Reminder',
    description: 'Send automated payment reminders before and after due dates',
    trigger_event: 'invoice_due_soon',
  },
  {
    name: 'Overdue Invoice Escalation',
    description: 'Escalate overdue invoices with increasing urgency',
    trigger_event: 'invoice_overdue',
  },
  {
    name: 'Project Status Update',
    description: 'Notify clients when project status changes',
    trigger_event: 'project_status_changed',
  },
  {
    name: 'Weekly Progress Report',
    description: 'Send weekly progress summaries to clients',
    trigger_event: 'weekly_schedule',
  },
  {
    name: 'Meeting Reminder Sequence',
    description: 'Send reminders before scheduled meetings',
    trigger_event: 'meeting_scheduled',
  },
  {
    name: 'Service Request Auto-Assignment',
    description: 'Automatically assign service requests to team members',
    trigger_event: 'service_request_created',
  },
];

export const ActivateAllTemplates = () => {
  const queryClient = useQueryClient();

  const activateMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get existing workflows
      const { data: existing } = await supabase
        .from('automation_workflows')
        .select('name');

      const existingNames = new Set(existing?.map(w => w.name) || []);

      // Insert only workflows that don't exist
      const newWorkflows = TEMPLATE_WORKFLOWS.filter(
        template => !existingNames.has(template.name)
      ).map(template => ({
        user_id: user.id,
        name: template.name,
        description: template.description,
        trigger_event: template.trigger_event,
        actions: [],
        is_active: true,
      }));

      if (newWorkflows.length === 0) {
        return { message: 'All templates already activated', count: 0 };
      }

      const { error } = await supabase
        .from('automation_workflows')
        .insert(newWorkflows);

      if (error) throw error;

      return { message: `Activated ${newWorkflows.length} templates`, count: newWorkflows.length };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["automation-workflows"] });
      if (data.count > 0) {
        toast.success(data.message);
      } else {
        toast.info(data.message);
      }
    },
    onError: (error) => {
      toast.error("Failed to activate templates: " + error.message);
    },
  });

  return (
    <Button
      onClick={() => activateMutation.mutate()}
      disabled={activateMutation.isPending}
      variant="default"
      className="gap-2"
    >
      <Zap className={`h-4 w-4 ${activateMutation.isPending ? 'animate-spin' : ''}`} />
      {activateMutation.isPending ? 'Activating...' : 'Activate All Templates'}
    </Button>
  );
};
